import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';

import organisationsAPI from '@/api/organisations';
import Modal from '@/components/Modals/Modal';
import useAuth from '@/hooks/useAuth';

import Button, { BUTTON_KIND } from '../Button';

export function DeleteGroup({ groupID }: { groupID: number | undefined }) {
  const { orgID } = useAuth();
  const navigate = useNavigate();

  const [isDeleteConfirmed, setIsDeleteConfirmed] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { mutateAsync: deleteGroup, isPending: isDeletingGroup } = useMutation({
    mutationFn: deleteOrganisationGroup,
    onSuccess: () => {
      toast.success('Group has been deleted successfully.');
      navigate('/groups');
    },
    onError: () => {
      setIsModalOpen(true);
    },
  });

  async function deleteOrganisationGroup() {
    if (orgID === undefined) {
      throw new Error('No org ID');
    }

    if (groupID === undefined) {
      throw new Error('Group ID cannot be empty');
    }

    await organisationsAPI.deleteOrganisationGroup(orgID, groupID);
  }

  return (
    <>
      <div className="flex justify-end">
        <ul className="divide-y divide-gray-200">
          <Button
            buttonText="Delete"
            kind={BUTTON_KIND.LIGHT_RED}
            onClick={() => setIsModalOpen(true)}
            disabled={groupID === undefined}
          />
        </ul>
      </div>
      <Modal
        isOpen={isModalOpen}
        setIsOpen={setIsModalOpen}
        dialogTitle={
          <span className="font-bold">
            Warning:&nbsp;
            <span className="font-medium">
              Deleting this group is permanent.
            </span>
          </span>
        }
        dialogDescription="By proceeding, you acknowledge that you will permanently lose access to this group, associated data and analytics. Profile and group managers will not be deleted."
        successButtonText="Delete group"
        successButtonKind={BUTTON_KIND.RED}
        isLoading={isDeletingGroup}
        onSuccess={deleteGroup}
        checkbox={isDeleteConfirmed}
        setCheckbox={setIsDeleteConfirmed}
        isDisabled={!isDeleteConfirmed}
        checkboxDescription="I am sure I want to delete this group."
      />
    </>
  );
}
